import {
    ClientDetailsPayloadInterface,
    InteractionPayloadType
} from 'storeTypes'

import { getLastMondayDate } from './getLastMondayDate'
import {
    findInteraction,
    isFarmerAwareness
} from './interactions/interactionHelper'
import {
    buildClientFilter,
    buildPotentialClientFilter,
    LAST_30TH_DAY,
    TWO_YEARS_APART
} from 'pages/village/villageFilterOptions'
import { InteractionOutcomes } from 'types'
import { PRODUCT_CATEGORY } from './constants'
import { capitalizeWord } from './capitalizeWord'

const isThisWeek = (date: string | undefined) => {
    if (!date) {
        return false
    }

    return Date.parse(date) >= getLastMondayDate().getTime()
}

const visitedOutcomes = {
    [InteractionOutcomes.FOLLOW_UP]: true,
    [InteractionOutcomes.NOT_INTERESTED]: true
}

export const activeClientList = (client: ClientDetailsPayloadInterface) =>
    new Date(client.last_voucher_date).getTime() >= LAST_30TH_DAY &&
    client.balance < client.all_enrollment

export const inactiveClientList = (client: ClientDetailsPayloadInterface) =>
    new Date(client.last_voucher_date).getTime() < LAST_30TH_DAY &&
    client.all_enrollment - client.balance > 0

export const completedPaymentClientList = (
    client: ClientDetailsPayloadInterface
) => client.all_enrollment - client.balance <= 0

export const neverDeliveredClients = (client: ClientDetailsPayloadInterface) =>
    !client.recent_year_delivered

export const beforeYearPrevClients = (client: ClientDetailsPayloadInterface) =>
    client.recent_year_delivered &&
    client.recent_year_delivered < TWO_YEARS_APART

export const clientByLastDelivered = (selectedYear: number) => {
    return (client: ClientDetailsPayloadInterface) =>
        client.recent_year_delivered === selectedYear
}

export const prevClientPayment = (client: ClientDetailsPayloadInterface) =>
    (client.smss || []).length > 0

export const visitedThisWeek =
    (interactions: InteractionPayloadType[]) =>
    (client: ClientDetailsPayloadInterface) => {
        return interactions.some((interaction) => {
            return (
                interaction.client_code === client.client_code &&
                isFarmerAwareness(interaction) &&
                isThisWeek(interaction.date_submitted || interaction.date)
            )
        })
    }

export const visitedAnytime =
    (interactions: InteractionPayloadType[]) =>
    ({ duplicate, client_code: clientCode }: ClientDetailsPayloadInterface) =>
        !duplicate &&
        interactions?.some(
            ({ client_code: interactionClientCode, outcome }) =>
                interactionClientCode === clientCode &&
                outcome &&
                outcome in visitedOutcomes
        )

export const notInterested = (interactions: InteractionPayloadType[] = []) => {
    return (client: ClientDetailsPayloadInterface) => {
        if (client.duplicate) {
            return false
        }

        const latestInteraction = findInteraction(
            interactions,
            client.client_code
        )

        return (
            latestInteraction?.outcome === InteractionOutcomes.NOT_INTERESTED
        )
    }
}

export const visitedNever = (interactions: InteractionPayloadType[]) => {
    return (client: ClientDetailsPayloadInterface) =>
        !visitedAnytime(interactions)(client) && !client.duplicate
}

export const packageNoPaymentClients = (
    client: ClientDetailsPayloadInterface
) => client.all_enrollment > 0 && client.balance <= 0 && !client.duplicate

export const paymentNoPackageClients = (
    client: ClientDetailsPayloadInterface
) => client.all_enrollment === 0 && client.balance > 0 && !client.duplicate

export const duplicateClientFilter = (client: ClientDetailsPayloadInterface) =>
    client?.duplicate

export const filterClientByLowPaymentThreshold = (
    client: ClientDetailsPayloadInterface
) =>
    client.payment_progress > 0 &&
    client.payment_progress >= 25 &&
    client.payment_progress < 50

export const filterClientByMediumPaymentThreshold = (
    client: ClientDetailsPayloadInterface
) => client.payment_progress >= 50 && client.payment_progress < 75

export const filterClientByHighPaymentThreshold = (
    client: ClientDetailsPayloadInterface
) => client.payment_progress >= 75 && client.payment_progress < 100

export const filterByClientCategory =
    (category: string) => (client: ClientDetailsPayloadInterface) =>
        client.categories?.includes(category)

export const buildClientFilters = (
    t: (key: string) => string,
    interactions: InteractionPayloadType[] = []
) => {
    const clientFilter = buildClientFilter(t)
    const potentialClientFilter = buildPotentialClientFilter(t)

    const mappedCategoriesToFilterFunction = Object.fromEntries(
        Object.values(PRODUCT_CATEGORY).map((category) => [
            category,
            filterByClientCategory(capitalizeWord(category))
        ])
    )

    return {
        [clientFilter.ACTIVE]: activeClientList,
        [clientFilter.INACTIVE]: inactiveClientList,
        [clientFilter.COMPLETED_PAYMENT]: completedPaymentClientList,
        [clientFilter.PACKAGE_NO_PAYMENT]: packageNoPaymentClients,
        [clientFilter.LOW_PAYMENT_THRESHOLD]: filterClientByLowPaymentThreshold,
        [clientFilter.MEDIUM_PAYMENT_THRESHOLD]:
            filterClientByMediumPaymentThreshold,
        [clientFilter.HIGH_PAYMENT_THRESHOLD]:
            filterClientByHighPaymentThreshold,
        [clientFilter.FINISHER_CLIENT]: completedPaymentClientList,
        ...mappedCategoriesToFilterFunction,
        [potentialClientFilter.NEVER_DELIVERED]: neverDeliveredClients,
        [potentialClientFilter.BEFORE_YEAR]: beforeYearPrevClients,
        [potentialClientFilter.CLIENT_PAYMENT]: prevClientPayment,
        previousYear: clientByLastDelivered,
        [potentialClientFilter.VISITED_THIS_WEEK]:
            visitedThisWeek(interactions),
        [potentialClientFilter.VISITED]: visitedAnytime(interactions),
        [potentialClientFilter.NOT_VISITED]: visitedNever(interactions),
        [potentialClientFilter.PAYMENT_NO_PACKAGE]: paymentNoPackageClients,
        [potentialClientFilter.DUPLICATE]: duplicateClientFilter,
        [potentialClientFilter.NOT_INTERESTED]: notInterested(interactions)
    }
}
